import * as React from 'react';

import './index.scss';

import Toolbar from './Toolbar';
import PropertyBar from './PropertyBar';
import PathEditBar from './PathEditBar/index';
import FontLoadingIndicator from './FontLoadingIndicator';
import BottomToolbar from './BottomToolbar';
import ShortcutCheatsheet from './ShortcutCheatsheet';
import { App } from '../../../pkg';
import { registerResize } from '../../utils/dom-events';
import { makeLooper } from '../../utils/loop';
import Fps from './Fps';
import Textarea from './Textarea';
import { CursorDrawMode } from '../../models/cursor';
import { registerEventReceiver, onTextEdit } from '../../wasmEventReceiver';
import { selectImage } from '../../utils/file';
import { imageStorage } from '../../service/image_storage';
import { getCanvasSizeInfo } from '../../utils/dom-utils';
import { mapKey } from '../../utils/key';
import { FontManager } from '../../utils/fontManager';
import { editorStorage } from '../../service/editor_storage';

interface IApplicationProps {
  app: App;
  fontManager: FontManager;
}

interface IPathEditingAppApi {
  is_path_editing?: () => boolean;
  is_path_closed?: () => boolean;
  close_editing_path?: () => void;
  finish_path_editing?: () => void;
  get_selection_count?: () => number;
}

interface IPersistAppApi {
  export_editor_json?: () => string;
  export_camera_json?: () => string;
}

type TextEditState = null | {
  viewLeft: number;
  viewTop: number;
  text: string;
  elementId?: string;
  fontSize?: number;
  color?: string;
  fontFamily?: string;
};

const AUTOSAVE_DEBOUNCE_MS = 600;
const AUTOSAVE_IDLE_FALLBACK_MS = 80;
const AUTOSAVE_IDLE_TIMEOUT_MS = 1000;

enum MouseEventType {
  Down,
  Up,
  Move,
  DblClick,
}

const getCanvasPositionFromClient = (
  clientX: number,
  clientY: number,
): { x: number; y: number; rect: DOMRect } | null => {
  const canvas = window.document.querySelector('canvas.app');
  if (!(canvas instanceof HTMLCanvasElement)) {
    return null;
  }
  const rect = canvas.getBoundingClientRect();
  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
    rect,
  };
};

const toScreenPositionFromCanvas = (x: number, y: number): { left: number; top: number } => {
  const canvas = window.document.querySelector('canvas.app');
  if (!(canvas instanceof HTMLCanvasElement)) {
    return { left: x, top: y };
  }
  const rect = canvas.getBoundingClientRect();
  return {
    left: x + rect.left,
    top: y + rect.top,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getTextInfoValue = (textInfo: Map<string, any> | Record<string, any>, key: string): unknown =>
  textInfo instanceof Map ? textInfo.get(key) : textInfo[key];

const resetAppSize = (app: App): void => {
  const sizeInfo = getCanvasSizeInfo(0);
  app.handle_resize(sizeInfo.width, sizeInfo.height, sizeInfo.dpr);
};

const formatShortcutKey = (key: string): string => {
  if (key === ' ') {
    return 'Space';
  }
  if (key.length === 1) {
    return key.toUpperCase();
  }
  if (key === 'Escape') {
    return 'Esc';
  }
  return key;
};

const detectMacLikePlatform = (): boolean => {
  const platform = navigator.platform ?? '';
  const userAgent = navigator.userAgent ?? '';
  return /mac|iphone|ipad|ipod/i.test(`${platform} ${userAgent}`);
};

const formatShortcutKeyForPlatform = (key: string, isMacLike: boolean): string => {
  if (!isMacLike) {
    return formatShortcutKey(key);
  }
  if (key === ' ') {
    return '␣';
  }
  if (key === 'Escape') {
    return '⎋';
  }
  if (key === 'Enter') {
    return '↩';
  }
  if (key === 'Backspace' || key === 'Delete') {
    return '⌫';
  }
  if (key.length === 1) {
    return key.toUpperCase();
  }
  return key;
};

const formatShortcutFromEvent = (e: KeyboardEvent): string | null => {
  if (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta') {
    return null;
  }
  const isMacLike = detectMacLikePlatform();
  const modifiers: string[] = [];
  if (isMacLike) {
    if (e.metaKey) {
      modifiers.push('⌘');
    }
    if (e.shiftKey) {
      modifiers.push('⇧');
    }
    if (e.altKey) {
      modifiers.push('⌥');
    }
    if (e.ctrlKey) {
      modifiers.push('⌃');
    }
  } else {
    if (e.ctrlKey) {
      modifiers.push('Ctrl');
    }
    if (e.metaKey) {
      modifiers.push('Meta');
    }
    if (e.shiftKey) {
      modifiers.push('Shift');
    }
    if (e.altKey) {
      modifiers.push('Alt');
    }
  }
  const key = formatShortcutKeyForPlatform(e.key, isMacLike);
  if (isMacLike) {
    return `${modifiers.join('')}${key}`;
  }
  return [...modifiers, key].join(' + ');
};

const Application: React.FC<IApplicationProps> = ({ app, fontManager }: IApplicationProps) => {
  const looper = React.useMemo(() => makeLooper(app), [app]);
  const [currentMode, setCurrentMode] = React.useState<CursorDrawMode>(CursorDrawMode.Cursor);
  const [version, setVersion] = React.useState(0);
  const pathEditingApi = app as unknown as IPathEditingAppApi;
  const [editText, setEditText] = React.useState<TextEditState>(null);
  const isPathEditing = pathEditingApi.is_path_editing?.() ?? false;
  const isPathClosed = pathEditingApi.is_path_closed?.() ?? false;
  const selectionCount = pathEditingApi.get_selection_count?.() ?? 0;
  const hasPropertyBar = !isPathEditing && selectionCount > 0;
  const autosaveTimerRef = React.useRef<number | null>(null);
  const autosaveIdleHandleRef = React.useRef<number | null>(null);
  const autosaveIdleFallbackTimerRef = React.useRef<number | null>(null);
  const autosaveInFlightRef = React.useRef(false);
  const autosaveRerunRef = React.useRef(false);
  const lastCanvasPointerRef = React.useRef<{ x: number; y: number } | null>(null);
  const pendingMouseMoveRef = React.useRef<null | {
    x: number;
    y: number;
    shift: boolean;
    alt: boolean;
    ctrl: boolean;
  }>(null);
  const pendingMouseMoveRafRef = React.useRef<number | null>(null);
  const activePointerIdRef = React.useRef<number | null>(null);
  const pointerCaptureTargetRef = React.useRef<HTMLElement | null>(null);
  const shortcutHintTimerRef = React.useRef<number | null>(null);
  const [lastTriggeredShortcut, setLastTriggeredShortcut] = React.useState<string | null>(null);
  const [isShortcutCheatsheetOpen, setIsShortcutCheatsheetOpen] = React.useState(false);
  const [isFpsPanelVisible, setIsFpsPanelVisible] = React.useState(false);
  const hasCommittedTextEditRef = React.useRef(false);
  const editTextRef = React.useRef<TextEditState>(null);

  React.useEffect(() => {
    editTextRef.current = editText;
  }, [editText]);

  const beginTextEditing = React.useCallback(
    (payload: {
      viewLeft: number;
      viewTop: number;
      text: string;
      elementId?: string;
      fontSize?: number;
      color?: string;
      fontFamily?: string;
    }) => {
      hasCommittedTextEditRef.current = false;
      setEditText(payload);
    },
    [],
  );

  const openImagePicker = React.useCallback(async () => {
    const imageFile = await selectImage();
    const contentId = await imageStorage.saveImage(imageFile);
    const record = await imageStorage.getImage(contentId);
    if (record) {
      app.create_image(contentId, record.width, record.height);
      looper.requestRender();
      setVersion(v => v + 1);
    }
  }, [app, looper]);

  const flushAutosave = React.useCallback(async () => {
    if (autosaveInFlightRef.current) {
      autosaveRerunRef.current = true;
      return;
    }

    autosaveInFlightRef.current = true;
    try {
      const persistAppApi = app as unknown as IPersistAppApi;
      const editorJson = persistAppApi.export_editor_json?.();
      const cameraJson = persistAppApi.export_camera_json?.();
      if (editorJson && cameraJson !== undefined) {
        await editorStorage.persistFromEditorJson(editorJson, cameraJson);
      }
    } catch (error) {
      console.error('Editor autosave failed:', error);
    } finally {
      autosaveInFlightRef.current = false;
      if (autosaveRerunRef.current) {
        autosaveRerunRef.current = false;
        void flushAutosave();
      }
    }
  }, [app]);

  const cancelAutosaveIdleTask = React.useCallback(() => {
    if (autosaveIdleHandleRef.current !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(autosaveIdleHandleRef.current);
    }
    autosaveIdleHandleRef.current = null;
    if (autosaveIdleFallbackTimerRef.current !== null) {
      window.clearTimeout(autosaveIdleFallbackTimerRef.current);
      autosaveIdleFallbackTimerRef.current = null;
    }
  }, []);

  const scheduleAutosaveFlush = React.useCallback(() => {
    cancelAutosaveIdleTask();
    if (typeof window.requestIdleCallback === 'function') {
      autosaveIdleHandleRef.current = window.requestIdleCallback(
        () => {
          autosaveIdleHandleRef.current = null;
          void flushAutosave();
        },
        { timeout: AUTOSAVE_IDLE_TIMEOUT_MS },
      );
      return;
    }
    autosaveIdleFallbackTimerRef.current = window.setTimeout(() => {
      autosaveIdleFallbackTimerRef.current = null;
      void flushAutosave();
    }, AUTOSAVE_IDLE_FALLBACK_MS);
  }, [cancelAutosaveIdleTask, flushAutosave]);

  const scheduleAutosave = React.useCallback(() => {
    if (autosaveTimerRef.current !== null) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      scheduleAutosaveFlush();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [scheduleAutosaveFlush]);

  const commitTextEditing = React.useCallback(() => {
    if (hasCommittedTextEditRef.current) {
      return;
    }
    hasCommittedTextEditRef.current = true;

    const activeEditText = editTextRef.current;
    if (activeEditText && activeEditText.text.trim()) {
      if (activeEditText.elementId) {
        app.update_text_node(activeEditText.elementId, activeEditText.text.trim());
      } else {
        app.create_text_node(
          activeEditText.text.trim(),
          activeEditText.viewLeft,
          activeEditText.viewTop,
        );
      }
    }
    app.set_editing_id(undefined);
    editTextRef.current = null;
    setEditText(null);
    app.change_cursor_draw_mode(CursorDrawMode.Cursor);
    looper.requestRender();
  }, [app, looper]);

  React.useEffect(() => {
    looper.start();
    registerEventReceiver(evt => {
      if (evt === 'open_image_picker') {
        void openImagePicker();
        return;
      }
      if (evt === 'new_mode') {
        setCurrentMode(app.get_cursor_draw_mode());
      } else if (evt === 'editor_changed') {
        setVersion(v => v + 1);
        scheduleAutosave();
      } else if (evt === 'fonts_changed') {
        setVersion(v => v + 1);
      }
      looper.requestRender();
    });

    // 监听文字编辑事件
    const unsubscribeTextEdit = onTextEdit((elementId: string) => {
      const textInfo = app.get_text_element_info(elementId);
      
      if (textInfo) {
        const x = Number(getTextInfoValue(textInfo, 'x'));
        const y = Number(getTextInfoValue(textInfo, 'y'));
        
        if (!isNaN(x) && !isNaN(y)) {
          const idValue = getTextInfoValue(textInfo, 'id');
          const id = typeof idValue === 'string' ? idValue : undefined;
          app.set_editing_id(id); // 通知 Rust 隐藏该元素
          looper.requestRender();
          beginTextEditing({
            viewLeft: x,
            viewTop: y,
            text: String(getTextInfoValue(textInfo, 'text') ?? ''),
            elementId: id,
            fontSize: Number(getTextInfoValue(textInfo, 'fontSize')) || undefined,
            color: String(getTextInfoValue(textInfo, 'fillColor') ?? ''),
            fontFamily: String(getTextInfoValue(textInfo, 'fontFamily') ?? ''),
          });
        } else {
          console.error('Invalid coordinates in textInfo:', textInfo);
        }
      }
    });

    return () => {
      if (autosaveTimerRef.current !== null) {
        window.clearTimeout(autosaveTimerRef.current);
      }
      cancelAutosaveIdleTask();
      if (shortcutHintTimerRef.current !== null) {
        window.clearTimeout(shortcutHintTimerRef.current);
      }
      unsubscribeTextEdit();
    };
  }, [app, beginTextEditing, cancelAutosaveIdleTask, fontManager, openImagePicker, scheduleAutosave]);

  React.useEffect(() => {
    resetAppSize(app);
    looper.requestRender();
    const resizeDebounceMs = 60;
    let resizeTimer: number | null = null;

    const handleResize = () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(() => {
        resizeTimer = null;
        resetAppSize(app);
        looper.requestRender();
      }, resizeDebounceMs);
    };
    const customResizeEventName = registerResize();
    window.addEventListener(customResizeEventName, handleResize);

    return () => {
      if (resizeTimer !== null) {
        window.clearTimeout(resizeTimer);
      }
      window.removeEventListener(customResizeEventName, handleResize);
    };
  }, [app, looper]);

  React.useEffect(() => {
    if (!editText?.elementId) {
      return;
    }
    const textInfo = app.get_text_element_info(editText.elementId);
    if (!textInfo) {
      return;
    }
    const nextViewLeft = Number(getTextInfoValue(textInfo, 'x'));
    const nextViewTop = Number(getTextInfoValue(textInfo, 'y'));
    if (isNaN(nextViewLeft) || isNaN(nextViewTop)) {
      return;
    }
    const nextFontSize = Number(getTextInfoValue(textInfo, 'fontSize')) || undefined;
    const nextColor = String(getTextInfoValue(textInfo, 'fillColor') ?? '');
    const nextFontFamily = String(getTextInfoValue(textInfo, 'fontFamily') ?? '');
    setEditText(prev => {
      if (!prev || prev.elementId !== editText.elementId) {
        return prev;
      }
      if (
        Math.abs(prev.viewLeft - nextViewLeft) < 0.01 &&
        Math.abs(prev.viewTop - nextViewTop) < 0.01 &&
        prev.fontSize === nextFontSize &&
        prev.color === nextColor &&
        prev.fontFamily === nextFontFamily
      ) {
        return prev;
      }
      return {
        ...prev,
        viewLeft: nextViewLeft,
        viewTop: nextViewTop,
        fontSize: nextFontSize,
        color: nextColor,
        fontFamily: nextFontFamily,
      };
    });
  }, [app, editText?.elementId, version]);

  React.useEffect(() => {
    const isOverlayOrEditableTarget = (eventTarget: EventTarget | null): boolean => {
      if (!(eventTarget instanceof Element)) {
        return false;
      }
      if (eventTarget.closest('.canvas-overlay')) {
        return true;
      }
      return eventTarget.tagName === 'TEXTAREA' || eventTarget.tagName === 'INPUT';
    };

    const handleMouseEvent = (
      eventType: MouseEventType,
      updateVersion: boolean,
      event: MouseEvent | PointerEvent,
    ) => {
      if (isOverlayOrEditableTarget(event.target)) {
        return;
      }
      const canvasPosition = getCanvasPositionFromClient(event.clientX, event.clientY);
      if (!canvasPosition) {
        return;
      }
      const x = canvasPosition.x;
      const y = canvasPosition.y;
      lastCanvasPointerRef.current = { x, y };
      if (currentMode === CursorDrawMode.Text) {
        if (eventType === MouseEventType.Down) {
          if (!editTextRef.current) {
            beginTextEditing({
              viewLeft: x,
              viewTop: y,
              text: '',
            });
          } else {
            commitTextEditing();
          }
        }
        return;
      }
      const modifiers = {
        shift: event.shiftKey,
        alt: event.altKey,
        ctrl: event.metaKey || event.ctrlKey,
      };
      if (eventType === MouseEventType.Move) {
        pendingMouseMoveRef.current = { x, y, ...modifiers };
        if (pendingMouseMoveRafRef.current === null) {
          pendingMouseMoveRafRef.current = window.requestAnimationFrame(() => {
            pendingMouseMoveRafRef.current = null;
            const pending = pendingMouseMoveRef.current;
            if (!pending) {
              return;
            }
            pendingMouseMoveRef.current = null;
            app.handle_mouse(MouseEventType.Move, pending.x, pending.y, {
              shift: pending.shift,
              alt: pending.alt,
              ctrl: pending.ctrl,
            });
            looper.requestRender();
          });
        }
        return;
      }

      app.handle_mouse(eventType, x, y, modifiers);
      looper.requestRender();
      if (updateVersion) {
        setVersion(v => v + 1);
      }
    };

    const syncModifierStateToMouse = (e: KeyboardEvent) => {
      const lastPointer = lastCanvasPointerRef.current;
      if (!lastPointer) {
        return;
      }
      app.handle_mouse(MouseEventType.Move, lastPointer.x, lastPointer.y, {
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey || e.ctrlKey,
      });
      looper.requestRender();
    };

    const handleMouseDown = handleMouseEvent.bind(null, MouseEventType.Down, false);
    const releasePointerCapture = (pointerId: number) => {
      const captureTarget = pointerCaptureTargetRef.current;
      if (!captureTarget) {
        return;
      }
      if (!captureTarget.isConnected) {
        pointerCaptureTargetRef.current = null;
        return;
      }
      if (!captureTarget.hasPointerCapture(pointerId)) {
        pointerCaptureTargetRef.current = null;
        return;
      }
      try {
        captureTarget.releasePointerCapture(pointerId);
      } catch (_err) {
        // Ignore capture release failures and continue cleanup.
      } finally {
        pointerCaptureTargetRef.current = null;
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (!event.isPrimary) {
        return;
      }
      if (isOverlayOrEditableTarget(event.target)) {
        return;
      }
      activePointerIdRef.current = event.pointerId;
      const canvas = window.document.querySelector('canvas.app');
      if (canvas instanceof HTMLElement) {
        try {
          canvas.setPointerCapture(event.pointerId);
          pointerCaptureTargetRef.current = canvas;
        } catch (_err) {
          pointerCaptureTargetRef.current = null;
        }
      }
      handleMouseDown(event);
    };
    document.addEventListener('pointerdown', handlePointerDown);

    const handlePointerMove = (event: PointerEvent) => {
      if (!event.isPrimary) {
        return;
      }
      const activePointerId = activePointerIdRef.current;
      if (activePointerId !== null && activePointerId !== event.pointerId) {
        return;
      }
      handleMouseEvent(MouseEventType.Move, false, event);
    };
    document.addEventListener('pointermove', handlePointerMove);

    const handlePointerUp = (event: PointerEvent) => {
      if (!event.isPrimary) {
        return;
      }
      const activePointerId = activePointerIdRef.current;
      if (activePointerId !== null && activePointerId !== event.pointerId) {
        return;
      }
      handleMouseEvent(MouseEventType.Up, true, event);
      if (activePointerIdRef.current === event.pointerId) {
        releasePointerCapture(event.pointerId);
        activePointerIdRef.current = null;
      }
    };
    document.addEventListener('pointerup', handlePointerUp);

    const handlePointerCancel = (event: PointerEvent) => {
      if (!event.isPrimary) {
        return;
      }
      const activePointerId = activePointerIdRef.current;
      if (activePointerId !== null && activePointerId !== event.pointerId) {
        return;
      }
      handleMouseEvent(MouseEventType.Up, true, event);
      if (activePointerIdRef.current === event.pointerId) {
        releasePointerCapture(event.pointerId);
        activePointerIdRef.current = null;
      }
    };
    document.addEventListener('pointercancel', handlePointerCancel);

    const handleDblClick = handleMouseEvent.bind(null, MouseEventType.DblClick, true);
    document.addEventListener('dblclick', handleDblClick);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isShortcutCheatsheetOpen) {
        if (e.key === 'Escape') {
          setIsShortcutCheatsheetOpen(false);
        }
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // eslint-disable-next-line
      // @ts-ignore
      const tagName = e.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return;
      }
      if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === '?') {
        setIsShortcutCheatsheetOpen(true);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (e.key === 'Escape') {
          commitTextEditing();
        }
        return;
      }
      const selectionCountBefore = pathEditingApi.get_selection_count?.() ?? 0;
      const event_handled = app.handle_key_down({
        key:  mapKey(e.key),
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey || e.ctrlKey,
      });
      const selectionCountAfter = pathEditingApi.get_selection_count?.() ?? 0;
      if (selectionCountAfter !== selectionCountBefore) {
        setVersion(v => v + 1);
      }
      if (event_handled) {
        looper.requestRender();
      }
      if (event_handled) {
        const activeElement = document.activeElement;
        if (
          activeElement instanceof HTMLButtonElement &&
          activeElement.classList.contains('tool-bar-button')
        ) {
          activeElement.blur();
        }
        const shortcutText = formatShortcutFromEvent(e);
        if (shortcutText) {
          setLastTriggeredShortcut(shortcutText);
          if (shortcutHintTimerRef.current !== null) {
            window.clearTimeout(shortcutHintTimerRef.current);
          }
          shortcutHintTimerRef.current = window.setTimeout(() => {
            setLastTriggeredShortcut(null);
            shortcutHintTimerRef.current = null;
          }, 1200);
        }
        e.stopPropagation();
        e.preventDefault();
      }
      if (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta') {
        syncModifierStateToMouse(e);
      }
      // let captured = true;
      // if (e.metaKey) {
        // switch (e.key) {
          // case 'a':
            // app.select_all();
            // break;
          // case 'g':
            // app.group_selection();
            // break;
          // case 's':
            // app.save();
            // break;
          // case '0':
          // case '1':
          // case '2':
          // case '3':
          // case '4':
          // case '5':
            // app.set_viewport(e.key);
            // break;
          // case 'z':
            // if (e.shiftKey) {
              // app.handle_redo();
            // } else {
              // app.handle_undo();
            // }
            // break;
          // case 'c':
            // app.handle_copy();
            // break;
          // case 'x':
            // app.handle_cut();
            // break;
          // case 'v':
            // app.handle_paste();
            // break;
          // case 'd':
            // app.handle_clone();
            // break;
          // default:
            // captured = false;
            // break;
        // }
      // } else {
        // switch (e.key) {
          // case 'Backspace':
            // app.handle_delete();
            // break;
          // case 'r':
            // app.change_cursor_draw_mode(CursorDrawMode.Rectangle);
            // break;
          // case 'o':
            // app.change_cursor_draw_mode(CursorDrawMode.Oval);
            // break;
          // case 't':
            // app.change_cursor_draw_mode(CursorDrawMode.Text);
            // break;
          // case 'Escape':
            // app.handle_escape_key();
            // break;
          // default:
            // captured = false;
            // break;
        // }
      // }
    };
    document.addEventListener('keydown', handleKeyDown);

    const handleKeyUp = (e: KeyboardEvent) => {
      if (isShortcutCheatsheetOpen) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      // eslint-disable-next-line
      // @ts-ignore
      const tagName = e.target?.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        return;
      }
      if (currentMode === CursorDrawMode.Text) {
        if (e.key === 'Escape') {
          commitTextEditing();
        }
        return;
      }
      app.handle_key_up({
        key:  mapKey(e.key),
        shift: e.shiftKey,
        alt: e.altKey,
        ctrl: e.metaKey || e.ctrlKey,
      });
      looper.requestRender();
      if (e.key === 'Alt' || e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta') {
        syncModifierStateToMouse(e);
      }
    };
    document.addEventListener('keyup', handleKeyUp);

    const handleWheel = (event: WheelEvent) => {
      if (currentMode === CursorDrawMode.Text) {
        return;
      }
      if (event.target instanceof Element && event.target.closest('.canvas-overlay')) {
        return;
      }
      // 阻止浏览器默认行为（包括 Mac 上的后退手势和页面缩放）
      event.preventDefault();

      // Mac 触控板双指缩放通常会触发带有 ctrlKey 的 wheel 事件
      if (event.ctrlKey || event.metaKey) {
        const canvasPosition = getCanvasPositionFromClient(event.clientX, event.clientY);
        if (!canvasPosition) {
          return;
        }
        // deltaY 在缩放手势中通常较小，除以一个系数来控制灵敏度
        // 注意：方向可能需要反转，尝试 -event.deltaY
        app.handle_zoom(-event.deltaY / 100, canvasPosition.x, canvasPosition.y);
      } else {
        app.handle_wheel(-event.deltaX, -event.deltaY);
      }
      setVersion(v => v + 1);
      looper.requestRender();
    };
    document.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    return () => {
      if (activePointerIdRef.current !== null) {
        releasePointerCapture(activePointerIdRef.current);
        activePointerIdRef.current = null;
      }
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerCancel);
      document.removeEventListener('dblclick', handleDblClick);

      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);

      document.removeEventListener('wheel', handleWheel);
      if (pendingMouseMoveRafRef.current !== null) {
        window.cancelAnimationFrame(pendingMouseMoveRafRef.current);
        pendingMouseMoveRafRef.current = null;
      }
      pendingMouseMoveRef.current = null;
    };
  }, [
    app,
    beginTextEditing,
    commitTextEditing,
    currentMode,
    isShortcutCheatsheetOpen,
    looper,
  ]);

  return (
    <div className="application">
      <div className="left canvas-overlay">
        <Toolbar
          app={app}
          currentMode={currentMode}
          isFpsPanelVisible={isFpsPanelVisible}
          onToggleFpsPanel={() => {
            setIsFpsPanelVisible(prev => !prev);
          }}
          onOpenShortcutCheatsheet={() => {
            setIsShortcutCheatsheetOpen(true);
          }}
          onChangeMode={mode => {
            if (mode === CursorDrawMode.Image) {
              void openImagePicker();
            } else {
              app.change_cursor_draw_mode(mode);
            }
          }}
        />
      </div>
      {isPathEditing ? (
        <div className="top-overlay canvas-overlay">
          <PathEditBar
            isPathClosed={isPathClosed}
            onClosePath={() => {
              pathEditingApi.close_editing_path?.();
              setVersion(v => v + 1);
            }}
            onFinishEditing={() => {
              pathEditingApi.finish_path_editing?.();
              setVersion(v => v + 1);
            }}
          />
        </div>
      ) : null}
      {hasPropertyBar ? (
        <div className="top-overlay canvas-overlay">
          <PropertyBar
            app={app}
            version={version}
            onStyleChange={() => setVersion(v => v + 1)}
            onRequestRender={looper.requestRender}
          />
        </div>
      ) : null}
      {isFpsPanelVisible ? <Fps fps={looper.fps} /> : null}
      <FontLoadingIndicator fontManager={fontManager} />
      {lastTriggeredShortcut ? (
        <div className="shortcut-trigger-hint">
          <span className="shortcut-trigger-label">快捷键</span>
          <kbd>{lastTriggeredShortcut}</kbd>
        </div>
      ) : null}
      <div className="bottom-overlay">
        <BottomToolbar app={app} version={version} onRequestRender={looper.requestRender} />
      </div>
      <ShortcutCheatsheet
        isOpen={isShortcutCheatsheetOpen}
        onClose={() => {
          setIsShortcutCheatsheetOpen(false);
        }}
      />
      {editText
        ? (() => {
            const screenPosition = toScreenPositionFromCanvas(editText.viewLeft, editText.viewTop);
            return (
              <Textarea
                text={editText.text}
                left={screenPosition.left}
                top={screenPosition.top}
                fontSize={editText.fontSize}
                color={editText.color}
                fontFamily={editText.fontFamily}
                onChangeText={text => {
                  setEditText(prev => (prev ? { ...prev, text } : prev));
                }}
                onSubmit={() => {
                  commitTextEditing();
                }}
                onBlur={() => {
                  commitTextEditing();
                }}
              />
            );
          })()
        : null}
    </div>
  );
};

export default Application;
